import { xray } from 'lib/xray';
import { multiTariffDebugLogger } from 'reactApp/appHelpers/featuresHelpers/features/multiTariff';
import { overquotaThresholdLabel } from 'reactApp/appHelpers/featuresHelpers/features/overquotaStates';
import { getUserOverquotaState } from 'reactApp/modules/profile/profile.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import type { TabOption } from 'reactApp/sections/QuotaLanding/MultiTariffsList/hooks/useTabOptions';
import { store as reduxStore } from 'reactApp/store';
import { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';
import type { ITariffsByTab, TariffsByTabProductsType } from 'reactApp/ui/TariffSectionNew/TariffsSection.types';
import { getPlatformInfo } from 'reactApp/utils/gaHelpers';
import { stringHash } from 'reactApp/utils/stringHash';
import { captureException } from 'reactApp/utils/tracer';

export const getMultiTariffsListIds = ({ tab, period, tariffs }: { tab: string; period: ETabsName; tariffs: Record<string, any> }) => {
    const list = tariffs[tab] as ITariffsByTab[];

    if (!list) {
        return [];
    }

    return list.map((item) => {
        const { products } = item;

        const product = period === ETabsName.year ? products.yearProduct : products.monthProduct;

        return product?.id;
    });
};

const getSimpleTabTariffsConfig = (tabTariffs: { products: TariffsByTabProductsType }[]) => {
    const month: string[] = [];
    const year: string[] = [];

    tabTariffs.forEach((tariff) => {
        const monthTariffId = tariff?.products?.monthProduct?.id;
        if (monthTariffId) {
            month.push(monthTariffId);
        }
        const yearTariffId = tariff?.products?.yearProduct?.id;
        if (yearTariffId) {
            year.push(yearTariffId);
        }
    });

    return {
        month: month.sort(),
        year: year.sort(),
    };
};

export const getTabTariffsHash = (tabTariffs: { products: TariffsByTabProductsType }[]) => {
    const simplified = getSimpleTabTariffsConfig(tabTariffs);
    const hash = stringHash(JSON.stringify(simplified));

    return {
        original: tabTariffs,
        simplified,
        hash,
    };
};

const handledTabs = [ETabsName.basic, ETabsName.advanced, ETabsName.for_work, ETabsName.vigbo];

const getMultiTariffsListHash = (
    tariffsByTab: {
        basic?: { products: TariffsByTabProductsType }[];
        advanced?: { products: TariffsByTabProductsType }[];
        for_work?: { products: TariffsByTabProductsType }[];
        vigbo?: { products: TariffsByTabProductsType }[];
    },
    enabledTabsValues: ETabsName[]
) => {
    const enabledTabsTariffsInfo: Record<string, ReturnType<typeof getTabTariffsHash>> = {};

    handledTabs.forEach((tab) => {
        try {
            const hashInfo = getTabTariffsHash(tariffsByTab[tab]);

            if (enabledTabsValues.includes(tab)) {
                enabledTabsTariffsInfo[tab] = hashInfo;
            }
        } catch (error) {
            captureException(error, { issueKey: 'multiTariffAnalytics/hash', tab });
        }
    });

    return enabledTabsTariffsInfo;
};

export const sendMultiTariffHashAnalytics = (
    tariffListByTab: {
        basic?: { products: TariffsByTabProductsType }[];
        advanced?: { products: TariffsByTabProductsType }[];
        for_work?: { products: TariffsByTabProductsType }[];
        vigbo?: { products: TariffsByTabProductsType }[];
    },
    activeTariffTab: ETabsName,
    enabledTabs: TabOption[]
) => {
    const state = reduxStore.getState();
    const quotaState = getUserOverquotaState(state);
    // оверквота по занятому месту
    const isOverquota = UserQuotaSelectors.isOverquota(state);
    // состояние оверквоты по единому флоу
    const overquotaState = quotaState?.status === 'failed' ? 'failedapi' : quotaState?.state;
    const enabledTabsValues: ETabsName[] = enabledTabs.map((tab) => tab.value);
    const threshold = UserSelectors.getUserTreshold(state);

    if (!enabledTabsValues.includes(activeTariffTab)) {
        captureException(null, {
            message: 'No active tab',
            issueKey: 'multiTariff/threshold',
            tab: activeTariffTab,
        });
    }

    if (!enabledTabsValues.includes(ETabsName.advanced)) {
        captureException(null, {
            message: 'No advanced tab',
            issueKey: 'multiTariff/tabs',
        });
    }

    if (!threshold && threshold !== 0) {
        captureException(null, {
            message: 'No threshold',
            issueKey: 'multiTariff/threshold',
            threshold: String(threshold),
        });
    }

    const enabledTabsTariffsInfo = getMultiTariffsListHash(tariffListByTab, [...enabledTabsValues, activeTariffTab]);

    const hashes = {} as Record<ETabsName, string>;

    Object.keys(enabledTabsTariffsInfo).forEach(function (key) {
        hashes[key] = enabledTabsTariffsInfo[key].hash;
    });

    const dwhData = {
        ...hashes,
        overq: isOverquota,
        th: threshold || 'none',
        state: overquotaState || 0,
        label: overquotaThresholdLabel,
        platform: getPlatformInfo().join('_'),
    };

    const dwh_enabled_fields: (keyof typeof dwhData)[][] = Object.keys(hashes).reduce((accum, currTab) => {
        accum.push(['state', 'overq', 'th', currTab as ETabsName, 'platform']);

        if (overquotaThresholdLabel) {
            accum.push(['label', 'state', 'overq', 'th', currTab as ETabsName, 'platform']);
        }

        return accum;
    }, [] as (keyof typeof dwhData)[][]);

    multiTariffDebugLogger.info('hashes', {
        tariffListByTab,
        enabledTabs,
        enabledTabsTariffsInfo,
        activeTariffTab,
        threshold,
    });

    if (dwh_enabled_fields.length === 0) {
        return;
    }

    xray.send('multi-tariff-list', {
        dwh: dwhData,
        dwh_enabled: true,
        dwh_enabled_fields,
    });
};
